import { __, sprintf } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

/**
 * Helper: get the title from a single-value WPGraphQL post-type relationship.
 * Single pick fields come back as { node: { title } }.
 *
 * @param {Object} connection WPGraphQL connection object
 * @return {string} The title or empty string
 */
const postTitle = ( connection ) => connection?.node?.title ?? '';

/**
 * Format a Pods datetime string ("YYYY-MM-DD HH:MM:SS") to "DD/MM/YYYY HH:MM".
 * Returns '—' for empty/null values.
 *
 * @param {string|null} val
 * @return {string|null}
 */
function formatDatetime( val ) {
	if ( ! val ) {
		return null;
	}
	// Pods stores datetimes as "YYYY-MM-DD HH:MM:SS"
	const match = val.match( /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/ );
	if ( ! match ) {
		return val;
	}
	const [ , year, month, day, hour, min ] = match;
	return `${ day }/${ month }/${ year } ${ hour }:${ min }`;
}

/**
 * filterFn for post-type relationship columns: checks whether the selected
 * post title appears in the raw WPGraphQL connection stored on row.original[columnId].
 *
 * @param {Object} row
 * @param {string} columnId
 * @param {string} filterValue
 * @return {boolean}
 */
function postRefFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.node?.title === filterValue;
}
postRefFilterFn.autoRemove = ( val ) => ! val;

/**
 * Whether an activity's end (falling back to its start) date is in the past.
 * Activities with no date data at all are never considered past.
 *
 * @param {Object} row Raw GraphQL activity row (row.original).
 * @return {boolean}
 */
export function isActivityPast( row ) {
	const raw = row.dateEnd || row.dateStart;
	if ( ! raw ) {
		return false;
	}
	const match = raw.match( /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/ );
	if ( ! match ) {
		return false;
	}
	const [ , y, mo, d, h, mi ] = match;
	const dt = new Date( `${ y }-${ mo }-${ d }T${ h }:${ mi }:00` );
	return ! isNaN( dt.getTime() ) && dt.getTime() < Date.now();
}

/**
 * Column definitions for the activity CPT.
 *
 * Field names follow WPGraphQL for Pods convention: camelCase, no leading underscore.
 * Note: acDateStart / acDateEnd require wpgraphql_enabled: "1" in the Pods field config.
 * Verify against /graphql schema explorer if any field returns undefined.
 * @param root0
 * @param root0.onDelete
 * @param root0.mine     "My activities" mode (profile page): no staff controls, own registration status shown for everyone.
 */
export function activityColumns( { onDelete, mine = false } ) {
	const columns = [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && ! mine && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this activity to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( ( row ) => row.image?.node?.mediaItemUrl ?? '', {
			id: 'image',
			header: __( 'Image', 'anacrm' ),
			enableSorting: false,
			cell: ( { row, getValue } ) => {
				const url = getValue();
				if ( ! url ) {
					return null;
				}
				return (
					<img
						src={ url }
						alt={ row.original.image?.node?.altText || '' }
						loading="lazy"
						className="max-h-16 w-auto max-w-none"
					/>
				);
			},
		} ),

		col.accessor( 'title', {
			header: __( 'Activity', 'anacrm' ),
			cell: ( { row, getValue } ) => {
				const titleEl = (
					<a
						href={ row.original.uri }
						className="font-semibold text-blue hover:!no-underline"
					>
						{ getValue() }
					</a>
				);
				const idEl = window.anacrm?.isStaff && ! mine && (
					<span className="text-xs text-gray">
						{ sprintf(
							/* translators: %d: activity ID */
							__( '(ID: %d)', 'anacrm' ),
							row.original.databaseId
						) }
					</span>
				);

				// Sign-up button for non-staff users only (or the user's own
				// registration status in "My activities" mode, staff included).
				let signupEl = null;
				if (
					( mine || ! window.anacrm?.isStaff ) &&
					row.original.signup
				) {
					const reg = row.original._userRegistration;
					if ( reg ) {
						const isConfirmed =
							reg.statusId &&
							reg.statusId ===
								Number( window.anacrm?.confirmedStatusId );
						signupEl = (
							<div className="flex flex-col items-start gap-0.5">
								<button
									disabled
									className="mt-1 px-2 py-1 text-xs border-2 border-gray bg-gray-light text-gray cursor-not-allowed opacity-70"
								>
									{ reg.statusName ||
										__(
											'Registration pending confirmation',
											'anacrm'
										) }
								</button>
								{ isConfirmed && (
									<button
										onClick={ () =>
											row.original._onCancelRegistration?.()
										}
										className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold"
									>
										{ __(
											'Cancel registration',
											'anacrm'
										) }
									</button>
								) }
							</div>
						);
					} else if ( ! isActivityPast( row.original ) ) {
						signupEl = (
							<a
								href={ row.original.uri }
								className="mt-1 inline-block px-2 py-1 text-xs border-2 border-green-dark bg-green hover:bg-yellow !no-underline font-semibold"
							>
								{ __( 'Sign up', 'anacrm' ) }
							</a>
						);
					}
				}

				return (
					<div className="flex flex-col items-start gap-0.5">
						{ titleEl }
						{ idEl }
						{ signupEl }
					</div>
				);
			},
		} ),

		col.accessor( ( row ) => postTitle( row.project ), {
			id: 'project',
			header: __( 'Project', 'anacrm' ),
			filterFn: postRefFilterFn,
		} ),

		col.accessor(
			( row ) => {
				// Use activity's own orgRef if present; otherwise fall back to the
				// related project's orgRef (_pr_org_ref, multi-value).
				const direct = row.orgRef?.node?.title;
				if ( direct ) {
					return direct;
				}
				return (
					row.project?.node?.orgRef?.nodes
						?.map( ( n ) => n.title )
						.join( ', ' ) ?? ''
				);
			},
			{
				id: 'orgRef',
				header: __( 'Organisation', 'anacrm' ),
			}
		),

		col.accessor( 'dateStart', {
			header: __( 'Start', 'anacrm' ),
			cell: ( { getValue } ) => formatDatetime( getValue() ) ?? '—',
			sortingFn: 'alphanumeric',
		} ),

		col.accessor( 'dateEnd', {
			header: __( 'End', 'anacrm' ),
			cell: ( { getValue } ) => formatDatetime( getValue() ) ?? '—',
			sortingFn: 'alphanumeric',
		} ),

		col.accessor(
			( row ) => row.registrationCount ?? 0,
			{
				id: 'personRegistered',
				header: __( 'Registered people', 'anacrm' ),
				cell: ( { row, getValue } ) => {
					const count = getValue();
					const countEl = count > 0 ? count : '—';

					if ( ! window.anacrm?.isStaff ) {
						return countEl;
					}

					// Staff: show count + toggle button for the registrations sub-panel.
					return (
						<div className="flex flex-col items-start gap-1">
							<span>{ countEl }</span>
							<button
								onClick={ () => row.toggleExpanded() }
								className="px-2 py-1 text-xs border-2 border-green-dark bg-white hover:bg-yellow cursor-pointer font-semibold whitespace-nowrap"
							>
								{ row.getIsExpanded()
									? __( 'Close', 'anacrm' )
									: __( 'Manage registrations', 'anacrm' ) }
							</button>
						</div>
					);
				},
			}
		),
	];

	return mine
		? columns.filter( ( c ) => c.id !== 'personRegistered' )
		: columns;
}
